import { useNavigate } from "react-router-dom";
import { CountCard } from "../../../../components/CountCard/CountCard";
import Empty from "../../../../components/EmptyPagination/Empty";
import { ReactComponent as Search } from "src/assets/svg/search.svg";
import usePagination from "src/hooks/usePagination";
import { JobInterview, InterviewStatus } from "src/types";
import { getInterviewsPaginated } from "src/network/interview";
import PaginatedDiv from "src/components/PaginatedDiv";
import { InterviewMetrics, InterviewPaginatedParams } from "src/network/interview/type";
import { useState } from "react";
import useSlugParam from "src/hooks/useSlug";
import LoaderPage from "src/components/Loader/LoaderPage";
import Job from "src/types/models/Job";
import CopyButton from "src/components/CopyButton";
import { ReactComponent as CopyIcon } from "src/assets/svg/copy.svg";
import Tabs from "src/components/Tabs";

import Input from "src/components/Form/Input/Input";
import useDebounce from "src/hooks/useDebounce";
import InvitedTable from "src/components/Tables/InterviewTables/InvitedTable";
import InterviewedTable from "src/components/Tables/InterviewTables/InterviewedTable";
import ShortlistedTable from "src/components/Tables/InterviewTables/ShortlistedTable";
import { emptyInterviewTab } from "src/components/Tables/InterviewTables/empty";

type tab = "Invited" | "Interviewed" | "Shortlisted" | "Rejected";
const tabs: tab[] = ["Invited", "Interviewed", "Shortlisted", "Rejected"];
const tabFilters: { [key in tab]: InterviewPaginatedParams } = {
  Invited: { status: "INVITED" },
  Interviewed: { status: "COMPLETED", sort: [["ai_score", "DESC NULLS LAST"]] },
  Shortlisted: { status: "PASSED" },
  Rejected: { status: "FAILED" }
};

const Interviews = ({ metrics, job }: { metrics?: InterviewMetrics; job: Job }) => {
  const navigate = useNavigate();
  const { id, slug } = useSlugParam();
  const [filters, setFilters] = useState({ job_id: id, status: "INVITED", keyword: "" });
  const debouncedSetFilters = useDebounce(setFilters);
  const status = filters.status as InterviewStatus;
  const { data, count, fetching, fetch, reset } = usePagination<JobInterview>({
    fetchPage: getInterviewsPaginated,
    page_size: 10,
    filters
  });

  const emptyTab = emptyInterviewTab(status, slug, navigate);

  return (
    <>
      <div className="flex gap-16 mb-6">
        <CountCard color="green" title="Interviewed" count={metrics?.COMPLETED} />
        <CountCard color="yellow" title="Invited" count={metrics?.INVITED} />
        <CountCard color="green" title="Shorlisted" count={metrics?.PASSED} />
      </div>

      <Tabs
        style={1}
        className="mb-6"
        tabs={tabs}
        disabled={fetching}
        onChange={(s) => setFilters({ job_id: id, ...tabFilters[s] })}
      />

      <div className="flex justify-between">
        <Input
          wrapClassName=" !w-[370px]   !h-[56px] "
          className="!h-full  "
          onChange={(e) => debouncedSetFilters({ ...filters, keyword: e.target.value })}
          placeholder="Search by name or email..."
          icon={<Search className="w-6 h-6 " />}
        />

        <div className=" shrink-0 w-fit mb-6 px-4 py-2 rounded-lg border-[1px] border-[#CCC] flex gap-3 items-center  text-base font-semibold font-poppins">
          <p>Interview Link:</p>
          <p className="text-odfblue underline border-r-[1px] border-[#ccc] pr-3  overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px] ">
            {job.interview_url}
          </p>
          <CopyButton
            className="group border-[#ccc] border-[1px] rounded-lg flex gap-2 items-center justify-center p-2  hover:text-odfblue"
            text={job.interview_url}
          >
            <>
              <CopyIcon className="fill-black group-hover:fill-odfblue  w-4 h-4" /> Copy
            </>
          </CopyButton>
        </div>
      </div>

      {count === 0 && (
        <Empty
          className="flex-1 overflow-scroll"
          {...emptyTab[job.active ? "active" : "inactive"]}
        />
      )}

      {!data && <LoaderPage />}

      {data && !!count && (
        <PaginatedDiv
          className="!h-auto flex-1 "
          onLimit={fetch}
          loading={fetching}
          count={count}
          currentCount={data?.length}
        >
          {status === "INVITED" && <InvitedTable data={data} reset={reset} />}
          {status === "COMPLETED" && <InterviewedTable data={data} reset={reset} />}
          {status === "PASSED" && <ShortlistedTable data={data} reset={reset} />}
          {status === "FAILED" && <ShortlistedTable data={data} reset={reset} />}
        </PaginatedDiv>
      )}
    </>
  );
};

export default Interviews;
